import AppBar from "@material-ui/core/AppBar";
import CssBaseline from "@material-ui/core/CssBaseline";
import Divider from "@material-ui/core/Divider";
import Drawer from "@material-ui/core/Drawer";
import Hidden from "@material-ui/core/Hidden";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme,
} from "@material-ui/core/styles";
import Toolbar from "@material-ui/core/Toolbar";
import MenuIcon from "@material-ui/icons/Menu";
import clsx from "clsx";
import cookie from "js-cookie";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Route, Switch } from "react-router";
import { Link } from "react-router-dom";
import { refreshLoginToken, requestRelogin } from "../../redux/actions";
import { MainRoutes, SidebarOptions } from "../../routes/routes";
import Loader from "../components/Common/Loader";
import LoginConfirmDialog from "../components/Common/LoginConfirmDialog";
import MainLogo from "./../assets/main-logo.png";
import Header from "./Header";

const drawerWidth = 240;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
    },
    drawer: {
      [theme.breakpoints.up("sm")]: {
        width: drawerWidth,
        flexShrink: 0,
      },
    },
    appBar: {
      [theme.breakpoints.up("sm")]: {
        width: `calc(100% - ${drawerWidth}px)`,
        marginLeft: drawerWidth,
      },
    },
    menuButton: {
      marginRight: theme.spacing(2),
      [theme.breakpoints.up("sm")]: {
        display: "none",
      },
    },
    // necessary for content to be below app bar
    toolbar: {
      ...theme.mixins.toolbar,
      textAlign: "center",
    },
    drawerPaper: {
      width: drawerWidth,
    },
    content: {
      flexGrow: 1,
      padding: theme.spacing(3),
    },
    link: {
      textDecoration: "none",
      color: "inherit",
    },
  })
);

const MainLayout: React.FC<any> = ({ ...restProps }: any): JSX.Element => {
  const theme = useTheme();
  const classes = useStyles();
  const [selectedIndex, setSelectedIndex] = useState(0);

  const [mobileOpen, setMobileOpen] = useState(false);
  const dispatch = useDispatch();
  useEffect(() => {
    const token = cookie.get("token");
    // localStorage.getItem(AuthTokenKey);
    if (!token) {
      dispatch(requestRelogin());
    }
    if (token) {
      dispatch(refreshLoginToken());
    }

    const tokenExpires = cookie.get("tokenExpiry");
    // localStorage.getItem(AuthTokenExpirationKey);
    const interval = setInterval(() => {
      const currentTime = new Date().getTime();
      if (token && tokenExpires && parseInt(tokenExpires) - currentTime === 5) {
        dispatch(refreshLoginToken());
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const index = SidebarOptions.findIndex(({ route }) => route === restProps.location.pathname);
    if (index > -1) {
      setSelectedIndex(index);
    }
    // eslint-disable-next-line
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <div>
      <div className={classes.toolbar}>
        <a href={"https://www.writearn.in"}>
          <img src={MainLogo} alt={"Logo"} height={50} />
        </a>
      </div>
      <Divider />
      <List>
        {SidebarOptions.map(
          ({ icon: SidebarIcon, name: text, route, children }, index) => (
            <Link
              to={route}
              key={`${text}-${index}`}
              className={clsx(classes.link)}
              onClick={(e: any) => {
                setSelectedIndex(index);
                setMobileOpen(false);
              }}
            >
              <ListItem button key={text} selected={selectedIndex === index}>
                <ListItemIcon>
                  <SidebarIcon />
                </ListItemIcon>
                <ListItemText primary={text} />
              </ListItem>
            </Link>
          )
        )}
      </List>
    </div>
  );

  const container = document.body;

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            className={classes.menuButton}
          >
            <MenuIcon />
          </IconButton>
          <Header />
        </Toolbar>
      </AppBar>
      <nav className={classes.drawer} aria-label="mailbox folders">
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Hidden smUp implementation="css">
          <Drawer
            container={container}
            variant="temporary"
            anchor={theme.direction === "rtl" ? "right" : "left"}
            open={mobileOpen}
            onClose={handleDrawerToggle}
            classes={{
              paper: classes.drawerPaper,
            }}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
          >
            {drawer}
          </Drawer>
        </Hidden>
        <Hidden xsDown implementation="css">
          <Drawer
            classes={{
              paper: classes.drawerPaper,
            }}
            variant="permanent"
            open
          >
            {drawer}
          </Drawer>
        </Hidden>
      </nav>
      <main className={classes.content}>
        <div className={classes.toolbar} />
        <React.Suspense fallback={<Loader />}>
          <Switch>
            {MainRoutes.map(
              (
                { path, exact, component: Component, name, activeTab },
                index
              ) => (
                  <Route
                    key={index}
                    path={path}
                    exact={exact}
                    name={name}
                    render={(props: any) => (
                      <Component
                        {...props}
                        activeTab={activeTab}
                        {...restProps}
                      />
                    )}
                  />
                )
            )}
          </Switch>
          <LoginConfirmDialog />
        </React.Suspense>
      </main>
    </div>
  );
};

export default MainLayout;
